// A client's documents. Upload several files at once and watch each one go up; expired and
// expiring documents are flagged and listed first; the table itself is the drop target.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertCircle, Check, FileText, Loader2, Pencil, RotateCw, Sparkles, Trash2, Upload, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { useDocumentCategories } from '../../hooks/useSettings';
import {
  DataTable, Dialog, EmptyState, ErrorState, Field, FilterChips, OverflowMenu, SearchInput, Switch,
  fmtDate, plural,
} from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { dayOf, daysUntil } from './clientShared';

const MAX_BYTES = 20 * 1024 * 1024; // the server's limit for client documents
const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
const ACCEPT = ALLOWED_EXT.map((e) => `.${e}`).join(',');

const fileSize = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const extOf = (name) => String(name || '').split('.').pop().toLowerCase();

// Expired / expiring within 30 days / fine / no date.
const expiryState = (d) => {
  const n = daysUntil(d.expiry_date);
  if (n == null) return { key: 'none', n };
  if (n < 0) return { key: 'expired', n };
  if (n <= 30) return { key: 'expiring', n };
  return { key: 'ok', n };
};

function ExpiryCell({ doc }) {
  if (!doc.expiry_date) return <span className="text-slate-500">—</span>;
  const { key, n } = expiryState(doc);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 whitespace-nowrap">
      <span className="tabular-nums text-slate-700">{fmtDate(dayOf(doc.expiry_date))}</span>
      {key === 'expired' && <span className="badge-red !text-[11px]">Expired</span>}
      {key === 'expiring' && <span className="badge-yellow !text-[11px]">{n === 0 ? 'Expires today' : `Expires in ${plural(n, 'day')}`}</span>}
    </span>
  );
}

function EditDocDialog({ doc, categories, onSaved, onClose }) {
  const [form, setForm] = useState({ name: '', documentType: '', expiryDate: '' });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (doc) { setForm({ name: doc.name || '', documentType: doc.document_type || '', expiryDate: dayOf(doc.expiry_date) }); setError(null); }
  }, [doc]);

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!form.name.trim()) { setError('Enter a name'); return; }
    setSaving(true);
    try {
      await api.put(`/clients/${doc.client_id}/documents/${doc.id}`, form);
      toast.success('Document updated');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not update the document'); }
    finally { setSaving(false); }
  };

  const hasCategory = !form.documentType || categories.some((c) => c.name === form.documentType);
  return (
    <Dialog open={!!doc} onClose={onClose} title="Edit document details" size="sm"
      footer={<>
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" form="doc-edit-form" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save changes'}</button>
      </>}>
      <form id="doc-edit-form" onSubmit={save} className="space-y-4">
        <Field label="Name" required error={error}>
          {(fid) => <input id={fid} className="input-field" value={form.name} onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setError(null); }} />}
        </Field>
        <Field label="Category">
          {(fid) => (
            <select id={fid} className="input-field" value={form.documentType} onChange={(e) => setForm((f) => ({ ...f, documentType: e.target.value }))}>
              <option value="">No category</option>
              {!hasCategory && <option value={form.documentType}>{form.documentType}</option>}
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          )}
        </Field>
        <Field label="Expires" hint="Leave blank if it doesn’t expire.">
          {(fid) => <input id={fid} type="date" className="input-field" value={form.expiryDate} onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))} />}
        </Field>
      </form>
    </Dialog>
  );
}

let trayKey = 0;

export default function ClientDocuments() {
  const { id } = useParams();
  const record = useClientRecord(id);
  const confirm = useConfirm();
  const docCategories = useDocumentCategories('client');

  const [docs, setDocs] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  const [aiConsent, setAiConsent] = useState(true);
  // When the consent decision was recorded: none means it was never asked (shown as off), a date
  // with consent off means it was withdrawn.
  const [consentAt, setConsentAt] = useState(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const { user } = useAuth() || {};
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [tray, setTray] = useState([]); // { key, file, status, pct, error, doc, summary }
  const fileRef = useRef(null);
  const queue = useRef(Promise.resolve());
  const dragDepth = useRef(0);

  // Documents come with the client record; this page keeps its own copy so it can refresh it.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setState({ loading: true, error: null });
    try {
      const { data } = await api.get(`/clients/${id}`);
      setDocs(Array.isArray(data.documents) ? data.documents : []);
      setAiConsent(data.ai_processing_consent !== false);
      setConsentAt(data.ai_consent_recorded_at || null);
      setState({ loading: false, error: null });
    } catch (e) {
      if (!quiet) setState({ loading: false, error: e.response?.data?.message || 'We couldn’t load the documents. Check your connection, then try again.' });
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Record the client's decision on AI reading their documents (an admin, with who and when kept).
  const clientName = record?.client ? `${record.client.first_name || ''} ${record.client.last_name || ''}`.trim() : 'this client';
  const setConsent = async (on) => {
    const ok = await confirm(on
      ? `Only turn this on if ${clientName} (or their representative) has agreed to AI reading their documents. Scans and photos can then be read by AI, and "Analyse with AI" works. Your name and the time are recorded.`
      : `AI will no longer read ${clientName}'s documents. Files you upload are still stored, and text in PDFs and Word files is still read by Rostery itself.`,
    { title: on ? 'Record AI consent and turn AI on?' : 'Turn AI off for this client?', confirmLabel: on ? 'Consent given, turn on' : 'Turn off' });
    if (!ok) return;
    setConsentBusy(true);
    try {
      await api.put(`/compliance/ai-consent/${id}`, { consent: on });
      toast.success(on ? 'AI consent recorded' : 'AI turned off for this client');
      await load({ quiet: true });
      record?.reload?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the consent'); }
    finally { setConsentBusy(false); }
  };

  // ── Upload: every file chosen or dropped, one after another ───────────────
  const patchTray = (key, patch) => setTray((t) => t.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const uploadOne = useCallback(async (item) => {
    patchTray(item.key, { status: 'uploading', pct: 0, error: null });
    const fd = new FormData();
    fd.append('file', item.file);
    fd.append('clientId', id);
    try {
      const { data } = await api.post(`/clients/${id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (!ev.total) return;
          const pct = Math.round((ev.loaded / ev.total) * 100);
          patchTray(item.key, pct >= 100 ? { status: 'processing', pct: 100 } : { pct });
        },
      });
      const bits = [];
      if (data.filledFields?.length) bits.push(plural(data.filledFields.length, 'field') + ' filled');
      if (data.addedRisks) bits.push(plural(data.addedRisks, 'risk'));
      if (data.addedGoals) bits.push(plural(data.addedGoals, 'goal'));
      if (data.addedContacts) bits.push(plural(data.addedContacts, 'contact'));
      if (data.addedFunds) bits.push(plural(data.addedFunds, 'fund'));
      patchTray(item.key, { status: 'done', pct: 100, doc: data, summary: bits.length ? `AI added ${bits.join(', ')}` : null });
      load({ quiet: true });
      if (record.reload) record.reload();
    } catch (err) {
      patchTray(item.key, { status: 'error', error: err.response?.data?.message || 'The upload failed. Check your connection, then retry.' });
    }
  }, [id, load, record]);

  const enqueue = (item) => { queue.current = queue.current.then(() => uploadOne(item)); };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const items = files.map((file) => {
      const key = ++trayKey;
      if (!ALLOWED_EXT.includes(extOf(file.name))) return { key, file, status: 'error', pct: 0, error: 'This file type can’t be uploaded. Use PDF, Word, Excel, CSV, text or an image.', fatal: true };
      if (file.size > MAX_BYTES) return { key, file, status: 'error', pct: 0, error: 'This file is over 20 MB. Choose a smaller file.', fatal: true };
      return { key, file, status: 'queued', pct: 0, error: null };
    });
    setTray((t) => [...items, ...t]);
    items.filter((i) => i.status === 'queued').forEach(enqueue);
  };

  const retry = (item) => { patchTray(item.key, { status: 'queued', error: null, pct: 0 }); enqueue(item); };

  // A category picked in the tray is saved on the uploaded document straight away.
  const setTrayCategory = async (item, value) => {
    const doc = docs.find((d) => d.id === item.doc?.id) || item.doc;
    if (!doc) return;
    const prev = item.doc.document_type || '';
    patchTray(item.key, { doc: { ...item.doc, document_type: value } });
    try {
      await api.put(`/clients/${id}/documents/${doc.id}`, { name: doc.name, documentType: value, expiryDate: dayOf(doc.expiry_date) || null });
      load({ quiet: true });
    } catch (err) {
      patchTray(item.key, { doc: { ...item.doc, document_type: prev } });
      toast.error(err.response?.data?.message || 'Could not set the category');
    }
  };

  const busy = tray.some((t) => t.status === 'queued' || t.status === 'uploading' || t.status === 'processing');
  const clearFinished = () => setTray((t) => t.filter((x) => x.status === 'queued' || x.status === 'uploading' || x.status === 'processing'));

  // ── Drag and drop onto the documents area ─────────────────────────────────
  const dragProps = {
    onDragEnter: (e) => { if (!e.dataTransfer?.types?.includes('Files')) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); },
    onDragOver: (e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); },
    onDragLeave: () => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); },
    onDrop: (e) => { e.preventDefault(); dragDepth.current = 0; setDragging(false); addFiles(e.dataTransfer.files); },
  };

  // ── Row actions ────────────────────────────────────────────────────────────
  const setStaffVisible = async (doc, visible) => {
    setDocs((ds) => ds.map((x) => (x.id === doc.id ? { ...x, visible_to_staff: visible } : x)));
    try {
      await api.patch(`/clients/${id}/documents/${doc.id}/visibility`, { visible });
      toast.success(visible ? 'Shared with staff on this client’s shifts' : 'No longer shown to staff');
    } catch (e) {
      setDocs((ds) => ds.map((x) => (x.id === doc.id ? { ...x, visible_to_staff: !visible } : x)));
      toast.error(e.response?.data?.message || 'Could not change sharing');
    }
  };

  const setFamilyVisible = async (doc, visible) => {
    setDocs((ds) => ds.map((x) => (x.id === doc.id ? { ...x, visible_to_family: visible } : x)));
    try {
      await api.patch(`/clients/${id}/documents/${doc.id}/visibility`, { visible, audience: 'family' });
      toast.success(visible ? 'Shared with this client’s family portal' : 'No longer shown to family');
    } catch (e) {
      setDocs((ds) => ds.map((x) => (x.id === doc.id ? { ...x, visible_to_family: !visible } : x)));
      toast.error(e.response?.data?.message || 'Could not change sharing');
    }
  };

  const deleteDoc = async (doc) => {
    if (!await confirm(`“${doc.name}” is deleted for good.`, { title: 'Delete this document?', danger: true, confirmLabel: 'Delete document' })) return;
    try {
      await api.delete(`/clients/${id}/documents/${doc.id}`);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success('Document deleted');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete the document'); }
  };

  const analyzeDoc = async (doc) => {
    setAnalyzing(doc.id);
    const toastId = toast.loading('Reading the document…');
    try {
      const { data } = await api.post(`/clients/${id}/documents/${doc.id}/analyze`);
      const bits = [];
      if (data.filledFields?.length) bits.push(`${plural(data.filledFields.length, 'field')} filled`);
      if (data.addedRisks) bits.push(plural(data.addedRisks, 'risk'));
      if (data.addedGoals) bits.push(plural(data.addedGoals, 'goal'));
      if (data.addedContacts) bits.push(plural(data.addedContacts, 'contact'));
      if (data.addedFunds) bits.push(plural(data.addedFunds, 'fund'));
      toast.success(bits.length ? `AI: ${bits.join(' · ')}` : 'AI found nothing new in this document', { id: toastId, duration: 6000 });
      load({ quiet: true });
      if (record.reload) record.reload();
    } catch (e) {
      toast.error(e.response?.data?.message || 'The analysis failed', { id: toastId });
    } finally {
      setAnalyzing(null);
    }
  };

  // ── The list: expired and expiring first ───────────────────────────────────
  const counts = useMemo(() => docs.reduce((m, d) => {
    const k = expiryState(d).key;
    m[k] = (m[k] || 0) + 1;
    if (d.visible_to_staff) m.shared = (m.shared || 0) + 1;
    return m;
  }, {}), [docs]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rank = { expired: 0, expiring: 1, ok: 2, none: 3 };
    return docs
      .filter((d) => !q || `${d.name || ''} ${d.document_type || ''}`.toLowerCase().includes(q))
      .filter((d) => (filter === 'all' ? true : filter === 'shared' ? d.visible_to_staff : expiryState(d).key === filter))
      .map((d) => ({ ...d, _rank: rank[expiryState(d).key], _exp: dayOf(d.expiry_date) || null }))
      .sort((a, b) => a._rank - b._rank || String(a._exp || '').localeCompare(String(b._exp || '')) || String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }, [docs, search, filter]);

  const columns = [
    {
      key: 'name', header: 'Name', sortable: true,
      render: (d) => (
        <span className="flex items-center gap-2 min-w-0">
          <FileText size={15} className="text-slate-400 shrink-0" aria-hidden />
          {d.file_url
            // The session cookie opens the file; no token in the address.
            ? <a href={d.file_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary-700 hover:underline truncate">{d.name}</a>
            : <span className="text-slate-800 truncate">{d.name}</span>}
        </span>
      ),
    },
    { key: 'document_type', header: 'Category', sortable: true, render: (d) => (d.document_type ? <span className="badge-gray whitespace-nowrap">{d.document_type}</span> : <span className="text-slate-500">—</span>) },
    { key: '_rank', header: 'Expires', sortable: true, sortValue: (d) => d._exp, render: (d) => <ExpiryCell doc={d} /> },
    { key: 'created_at', header: 'Uploaded', sortable: true, render: (d) => <span className="tabular-nums text-slate-600 whitespace-nowrap">{d.created_at ? fmtDate(dayOf(d.created_at)) : '—'}</span> },
    {
      key: 'visible_to_staff', header: 'Shown to staff', sortable: true, sortValue: (d) => (d.visible_to_staff ? 1 : 0),
      render: (d) => (
        <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Switch checked={!!d.visible_to_staff} onChange={(v) => setStaffVisible(d, v)} label={`Show ${d.name} to staff rostered with this client`} />
          <span className={clsx('text-[12.5px]', d.visible_to_staff ? 'text-slate-700' : 'text-slate-500')}>{d.visible_to_staff ? 'Shown' : 'Hidden'}</span>
        </span>
      ),
    },
    {
      key: 'visible_to_family', header: 'Shown to family', sortable: true, sortValue: (d) => (d.visible_to_family ? 1 : 0),
      render: (d) => (
        <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Switch checked={!!d.visible_to_family} onChange={(v) => setFamilyVisible(d, v)} label={`Show ${d.name} in the Client & Family portal`} />
          <span className={clsx('text-[12.5px]', d.visible_to_family ? 'text-slate-700' : 'text-slate-500')}>{d.visible_to_family ? 'Shown' : 'Hidden'}</span>
        </span>
      ),
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: 48,
      render: (d) => (
        <span className="inline-flex items-center" onKeyDown={(e) => e.stopPropagation()}>
          {analyzing === d.id && <Loader2 size={14} className="animate-spin text-slate-400 mr-1" aria-label="Analysing" />}
          <OverflowMenu label={`Actions for ${d.name}`} items={[
            { label: 'Analyse with AI', icon: Sparkles, disabled: !aiConsent || analyzing === d.id, hint: aiConsent ? undefined : 'Consent off', onClick: () => analyzeDoc(d) },
            { label: 'Edit details', icon: Pencil, onClick: () => setEditing(d) },
            { divider: true },
            { label: 'Delete', icon: Trash2, danger: true, onClick: () => deleteDoc(d) },
          ]} />
        </span>
      ),
    },
  ];

  const chips = [
    { value: 'all', label: 'All', count: docs.length },
    { value: 'expiring', label: 'Expiring', count: counts.expiring || 0, tone: counts.expiring ? 'amber' : undefined },
    { value: 'expired', label: 'Expired', count: counts.expired || 0, tone: counts.expired ? 'rose' : undefined },
    { value: 'shared', label: 'Shown to staff', count: counts.shared || 0 },
  ];

  const uploadButton = (
    <button type="button" onClick={() => fileRef.current?.click()} className="btn-primary">
      <Upload size={15} aria-hidden /> Upload
    </button>
  );

  return (
    <ClientFrame record={record}>
      <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

      {!aiConsent && (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[13px] text-slate-600" role="note">
          <AlertCircle size={15} className="text-slate-400 mt-0.5 shrink-0" aria-hidden />
          <p className="flex-1 min-w-[240px]">
            {consentAt
              ? `AI is off for this client: AI-processing consent was withdrawn on ${new Date(consentAt).toLocaleDateString('en-AU')}.`
              : 'AI is off for this client: no AI-processing consent has been recorded.'}
            {' '}Uploaded documents are still stored, and text in PDFs and Word files is read by Rostery itself. Only scans and photos need AI.
          </p>
          {isAdmin && (
            <button type="button" onClick={() => setConsent(true)} disabled={consentBusy} className="btn-secondary btn-sm">
              {consentBusy ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Sparkles size={13} aria-hidden />} Record consent and turn AI on
            </button>
          )}
        </div>
      )}
      {aiConsent && isAdmin && (
        <p className="text-[12px] text-slate-500">
          AI can read this client's documents{consentAt ? ` (consent recorded ${new Date(consentAt).toLocaleDateString('en-AU')})` : ''}.{' '}
          <button type="button" onClick={() => setConsent(false)} disabled={consentBusy} className="underline hover:text-slate-700">Turn AI off for this client</button>
        </p>
      )}

      {/* Upload tray: one row per file */}
      {tray.length > 0 && (
        <section aria-label="Uploads" className="bg-white border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100">
            <h2 className="text-[13px] font-semibold text-slate-700">
              {busy ? `Uploading ${plural(tray.filter((t) => t.status !== 'done' && t.status !== 'error').length, 'file')}…` : 'Uploads'}
            </h2>
            {!busy && <button type="button" onClick={clearFinished} className="btn-ghost btn-sm">Clear</button>}
          </div>
          <ul className="divide-y divide-slate-100">
            {tray.map((t) => (
              <li key={t.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 truncate">{t.file.name}</p>
                  <p className={clsx('text-[12.5px]', t.status === 'error' ? 'text-rose-700' : 'text-slate-500')} role={t.status === 'error' ? 'alert' : undefined}>
                    {fileSize(t.file.size)}
                    {t.status === 'queued' && ' · Waiting…'}
                    {t.status === 'uploading' && ` · Uploading ${t.pct}%`}
                    {t.status === 'processing' && (aiConsent ? ' · Uploaded, AI is reading it…' : ' · Saving…')}
                    {t.status === 'done' && (t.summary ? ` · Uploaded. ${t.summary}` : ' · Uploaded')}
                    {t.status === 'error' && ` · ${t.error}`}
                  </p>
                  {(t.status === 'uploading' || t.status === 'processing') && (
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={t.pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${t.file.name} upload`}>
                      <div className={clsx('h-full rounded-full bg-primary-500 transition-[width] duration-200', t.status === 'processing' && 'animate-pulse')} style={{ width: `${t.pct}%` }} />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.status === 'done' && t.doc && (
                    <select aria-label={`Category for ${t.file.name}`} value={t.doc.document_type || ''} onChange={(e) => setTrayCategory(t, e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 bg-white pl-2.5 pr-8 text-[13px] text-slate-700">
                      <option value="">No category</option>
                      {t.doc.document_type && !docCategories.some((c) => c.name === t.doc.document_type) && <option value={t.doc.document_type}>{t.doc.document_type}</option>}
                      {docCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  )}
                  {t.status === 'done' && <Check size={16} className="text-emerald-600" aria-label="Uploaded" />}
                  {t.status === 'error' && !t.fatal && (
                    <button type="button" onClick={() => retry(t)} className="btn-secondary btn-sm"><RotateCw size={13} aria-hidden /> Retry</button>
                  )}
                  {(t.status === 'done' || t.status === 'error') && (
                    <button type="button" onClick={() => setTray((x) => x.filter((y) => y.key !== t.key))} aria-label={`Remove ${t.file.name} from the list`}
                      className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={15} aria-hidden /></button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* The documents; drop files anywhere on this card */}
      <section aria-label="Documents" {...dragProps}
        className={clsx('relative bg-white border rounded-xl', dragging ? 'border-primary-400 ring-2 ring-primary-200' : 'border-slate-200')}>
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-primary-400 bg-primary-50/80">
            <p className="flex items-center gap-2 text-sm font-semibold text-primary-700"><Upload size={16} aria-hidden /> Drop files to upload</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-3 border-b border-slate-100">
          <SearchInput value={search} onChange={setSearch} placeholder="Search documents" className="w-full sm:w-64" />
          <FilterChips options={chips} value={filter} onChange={(v) => setFilter(v || 'all')} multi={false} ariaLabel="Filter documents" />
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden md:inline text-[12.5px] text-slate-500">PDF, Word, Excel or images, up to 20 MB each</span>
            {uploadButton}
          </div>
        </div>

        {state.error ? (
          <ErrorState message={state.error} onRetry={() => load()} />
        ) : !state.loading && docs.length === 0 ? (
          <EmptyState icon={Upload} title="No documents yet"
            hint="Drag files here or choose Upload. Add the NDIS plan, service agreement and consent forms so workers and auditors can find them."
            action={uploadButton} />
        ) : (
          <DataTable columns={columns} rows={rows} rowKey="id" loading={state.loading} maxHeight="70vh"
            label="Client documents"
            empty={{ icon: FileText, title: 'No documents match', hint: 'Try another search or filter.' }} />
        )}
      </section>

      <EditDocDialog doc={editing ? { ...editing, client_id: id } : null} categories={docCategories}
        onSaved={() => { setEditing(null); load({ quiet: true }); }} onClose={() => setEditing(null)} />
    </ClientFrame>
  );
}
